import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCopyToClipboard } from 'react-use';

import InfoIconImage from 'assets/icons/info-icon.svg';
import { GlassBox } from 'components/glassBox/GlassBox';
import { BasicButton } from 'routes/staking_v2/components/BasicButton';

import { useClickOutside } from 'hooks/useClickOutside';
import { useFitTextEllipsis } from 'hooks/useFitTextEllipsis';
import { addSuccessMessageAction } from 'actions/global-actions';

import { UserStatusApiResponse } from 'routes/referral/types/user-status-api.types';
import { getRegistrationDateDisplay } from './getRegistrationDate';

type InfoRowProps = {
  label: string;
  value: string;
  href?: string;
  letterSpacing?: number;
};

const InfoRow = ({ label, value, href, letterSpacing }: InfoRowProps) => {
  const spacingStyle = letterSpacing ? { letterSpacing: `${letterSpacing}px` } : undefined;

  return (
    <div className="tw-flex tw-justify-between">
      <span>{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="tw-max-w-[150px] tw-text-white" style={spacingStyle}>
          {value}
        </a>
      ) : (
        <span className="tw-max-w-[150px]" style={spacingStyle}>{value}</span>
      )}
    </div>
  );
};

export type EventInfoPanelProps = {
  eventId: string;
  walletAddress: string;
  userStatus?: UserStatusApiResponse;
  variant?: 'mobile' | 'desktop';
};

export const EventInfoPanel = ({ eventId, walletAddress, userStatus, variant = 'desktop' }: EventInfoPanelProps) => {
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const dispatch = useDispatch();

  const panelRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const eventLink = window.location.href;
  const eventLinkDisplay = eventLink.replace(/^https?:\/\//, '');
  const registrationDate = getRegistrationDateDisplay(userStatus);
  const { displayText: displayWallet, measureRef: walletMeasureRef, letterSpacing: walletSpacing } = useFitTextEllipsis(walletAddress, 150);
  const { displayText: displayLink, measureRef: linkMeasureRef, letterSpacing: linkSpacing } = useFitTextEllipsis(eventLinkDisplay, 150, 'end');

  const handleClose = useCallback(() => {
    setIsContentVisible(false);
  }, []);

  useClickOutside(panelRef, handleClose, [iconRef], { closeOnScroll: true });

  const handleToggle = () => {
    setIsContentVisible((prev) => !prev);
  };

  const handleCopyAll = () => {
    const text = [
      `Event ID: ${eventId}`,
      `User ID: ${walletAddress}`,
      `Registered on: ${registrationDate}`,
      `Link: ${eventLink}`,
    ].join('\n');

    copyToClipboard(text);
    dispatch(addSuccessMessageAction('Copied to clipboard'));
  };

  const isMobile = variant === 'mobile';

  const rootClassName = isMobile
    ? 'tw-absolute tw-right-[20px] tw-flex tw-flex-col tw-items-end tw-mt-[1px] lg:tw-hidden'
    : 'tw-absolute tw-right-[56px] tw-flex tw-flex-col tw-items-end tw-mt-[10px] tw-hidden lg:tw-flex';

  const dropdownClassName = isMobile
    ? 'tw-fixed tw-left-[20px] tw-right-[20px] tw-mt-[45px] tw-z-[1]'
    : 'tw-absolute tw-top-full tw-right-0 tw-mt-[11px] tw-z-[1]';

  const glassBoxClassName = isMobile
    ? 'tw-w-full tw-p-[10px] tw-top-[10px]'
    : 'tw-w-[350px] tw-p-[10px] tw-top-[10px]';

  const arrowClassName = 'tw-absolute tw-top-[-10px] tw-right-[34px]';

  return (
    <div className={rootClassName}>
      <span
        ref={walletMeasureRef}
        className="tw-absolute tw-invisible tw-whitespace-nowrap tw-pointer-events-none tw-text-[16px] tw-font-normal tw-font-jost"
        aria-hidden="true"
      />
      <span
        ref={linkMeasureRef}
        className="tw-absolute tw-invisible tw-whitespace-nowrap tw-pointer-events-none tw-text-[16px] tw-font-normal tw-font-jost"
        aria-hidden="true"
      />
      <div ref={iconRef}>
        <img src={InfoIconImage} alt="Info" className="tw-cursor-pointer tw-mr-[32px] tw-w-[30px] tw-h-[30px]" onClick={handleToggle} />
      </div>

      <div
        ref={panelRef}
        className={`${dropdownClassName} tw-transition-opacity tw-duration-200 ${isContentVisible ? 'tw-opacity-100' : 'tw-opacity-0 tw-pointer-events-none'}`}
      >
        <svg
          width="27"
          height="22"
          viewBox="0 0 27 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={arrowClassName}
        >
          <path d="M13.5 0L27 22H0L13.5 0Z" fill="rgba(21, 17, 55, 0.40)" />
          <path d="M13.5 1L1 21" stroke="#2D5EFF" strokeWidth="1" fill="none" />
          <path d="M13.5 1L26 21" stroke="#2D5EFF" strokeWidth="1" fill="none" />
        </svg>
        <GlassBox
          gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.4) 100%)"
          borderWidth="1px"
          className={glassBoxClassName}
          backgroundOpacity={0.4}
        >
          <div className="tw-flex tw-flex-col tw-text-white tw-text-[16px] tw-font-normal tw-gap-[10px]">
            <InfoRow label="Event ID:" value={eventId} />
            <InfoRow label="User ID:" value={displayWallet || '-'} letterSpacing={walletSpacing} />
            <InfoRow label="Registered on:" value={registrationDate} />
            <InfoRow label="Link:" value={displayLink || '-'} href={eventLink} letterSpacing={linkSpacing} />
          </div>

          <BasicButton text="Copy All" onClick={handleCopyAll} className="tw-w-full tw-h-[40px] tw-mt-[10px]" />
        </GlassBox>
      </div>
    </div>
  );
};
